import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { ErrorModalHelperService } from '../../../../../_helpers/error-modal-helper/error-modal-helper.service';
import { RiskAssessment, RiskAssessmentAnswer } from '../../../../../_models/risk-assessment/risk-assessment.model';
import { RiskDefinition, RiskLookup, RiskSetting } from '../../../../../_models/risk-assessment/risk-lookup.model';
import { RiskAssessmentService } from '../../../../../_services/risk-assessment/risk-assessment.service';
import { DynamicConfirmModalComponent } from '../../../../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { FeatureSwitchKeys } from '../../../../../_constants/feature-switch-keys';
import { FeatureSwitchService } from '../../../../../_services/feature-switch/feature-switch.service';

export interface RiskAssessmentDialogData {
  placementId: number;
}

/** One rendered risk line — standard (from the lookup) or custom (educator-added, editable text). */
export interface RiskRow {
  riskDefinitionId: number | null;
  riskDescription: string;
  controlMeasures: string;
  riskLevel: string | null;
  custom: boolean;
  isCovered: boolean | null;
  additionalMeasures: string;
  showMeasures: boolean;
}

/**
 * One dropdown entry. A setting can hold several assessments (versions), so the list is keyed by
 * assessment — `set-{id}` for an existing one, `new-{settingId}` for a setting never assessed.
 */
export interface SetOption {
  key: string;
  label: string;
  setId: number | null;
  settingId: number;
}

/**
 * Placement risk assessment dialog (SDD/specs/RiskAssessmentFrontend.md, TutorPortalAPI repo).
 * One answer set per tuition setting per version; the dropdown lists every assessment (a setting can
 * hold several). Draft sets are editable; submitted sets render view-only from their saved (historical)
 * answer text, with New (supersede) + Unlock on the current version and nothing on superseded ones.
 * Closes with the placement's fresh RiskAssessment[] (or null) so the tile label can refresh.
 */
@Component({
  selector: 'risk-assessment-dialog',
  templateUrl: './risk-assessment-dialog.component.html',
  styleUrls: ['./risk-assessment-dialog.component.css'],
  standalone: false,
})
export class RiskAssessmentDialogComponent implements OnInit {
  public lookup: RiskLookup = { settings: [], risks: [] };
  public sets: RiskAssessment[] = [];
  public options: SetOption[] = [];
  /** The dropdown's value — `set-{id}` or `new-{settingId}`; diverges from the rendered set only while a switch awaits confirm. */
  public selectedKey: string | null = null;
  /** The rendered assessment's id. Null for a setting with no assessment yet. */
  public currentSetId: number | null = null;
  /** The setting being worked in — set even when no assessment exists yet. */
  public currentSettingId: number | null = null;
  /** The educator's acceptance of the responsibility declaration. Component state only: never saved with a draft. */
  public declarationAccepted = false;
  public standardRows: RiskRow[] = [];
  public customRows: RiskRow[] = [];
  public loading = true;
  public saving = false;
  public errorBannerText = '';
  public showOutlines = false;
  public lastSavedAt: Date | null = null;
  /** Download PDF (riskassessment.pdf, go-live snag 74). Off until the switch says on. */
  public pdfEnabled = false;
  public downloadingPdf = false;
  private savedSnapshot = '';
  /** True while the discard-confirm is open, so a mashed Escape cannot stack several of them. */
  private confirmingClose = false;

  constructor(
    public dialogRef: MatDialogRef<RiskAssessmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RiskAssessmentDialogData,
    private riskAssessmentService: RiskAssessmentService,
    private errorModalHelper: ErrorModalHelperService,
    private dialog: MatDialog,
    @Optional() private featureSwitches?: FeatureSwitchService
  ) {}

  ngOnInit(): void {
    this.featureSwitches?.isEnabled(FeatureSwitchKeys.riskAssessmentPdf)
      .subscribe(enabled => this.pdfEnabled = enabled === true);

    // The caller opens this dialog with disableClose: true, which also kills Material's own Escape
    // handling. Route Escape through close() instead of re-enabling it: that keeps the unsaved-changes
    // confirm, and closes with the fresh sets so the learner-journey tile still refreshes.
    this.dialogRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });

    forkJoin([
      this.riskAssessmentService.getLookup(),
      this.riskAssessmentService.getByPlacement(this.data.placementId),
    ]).subscribe({
      next: ([lookup, sets]) => {
        this.lookup = lookup;
        this.sets = sets;
        this.loading = false;
        this.buildOptions();
        const initialKey = this.initialKey(sets);
        if (initialKey != null) {
          this.selectOption(initialKey);
        }
      },
      error: (error) => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
        this.dialogRef.close(null);
      },
    });
  }

  public get currentSet(): RiskAssessment | undefined {
    return this.currentSetId == null ? undefined : this.sets.find((s) => s.id === this.currentSetId);
  }

  public get readonly(): boolean {
    return !!this.currentSet?.submitted;
  }

  public get activeSettings(): RiskSetting[] {
    return this.lookup.settings.filter((s) => s.isActive);
  }

  public get answeredCount(): number {
    return this.standardRows.filter((r) => r.isCovered !== null).length;
  }

  /** True when a newer version of the rendered set exists — it is historical evidence, not editable. */
  public get isSuperseded(): boolean {
    const set = this.currentSet;
    if (!set || set.riskSettingId == null) {
      return false;
    }
    return this.versionsOf(set.riskSettingId)[0]?.id !== set.id;
  }

  /** The version that replaced the rendered set, for the superseded banner. */
  public get supersededByVersion(): number | null {
    const set = this.currentSet;
    if (!set || set.riskSettingId == null || !this.isSuperseded) {
      return null;
    }
    return this.versionsOf(set.riskSettingId)[0]?.versionNumber ?? null;
  }

  /**
   * New is offered only on a SUBMITTED CURRENT assessment. A submitted current version implies no
   * draft exists for that setting (only the highest version can be a draft), which is exactly what the
   * server requires — so the button is never shown for an action the server would refuse.
   */
  public get canStartNew(): boolean {
    return this.readonly && !this.isSuperseded;
  }

  /** Drives the progress bar: an assessment with no saved answers is all-defaults, so `m of m` is noise. */
  public get hasSavedAnswers(): boolean {
    return (this.currentSet?.answers?.length || 0) > 0;
  }

  /** Sets for one setting, newest version first. */
  private versionsOf(settingId: number): RiskAssessment[] {
    return this.sets
      .filter((s) => s.riskSettingId === settingId)
      .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
  }

  /**
   * One option per existing assessment (newest version first), then each never-assessed active setting.
   * Settings are taken from the assessments as well as the lookup, so an assessment stays reachable
   * after its setting is deactivated — the evidence must remain viewable.
   */
  private buildOptions(): void {
    const assessedSettingIds = this.sets
      .map((s) => s.riskSettingId)
      .filter((id): id is number => id != null);
    const settingIds = Array.from(new Set([
      ...assessedSettingIds,
      ...this.activeSettings.map((s) => s.id),
    ]));

    const options: SetOption[] = [];
    settingIds.forEach((settingId) => {
      const versions = this.versionsOf(settingId);
      if (versions.length === 0) {
        options.push({
          key: `new-${settingId}`, label: `${this.settingName(settingId)} — Not started`,
          setId: null, settingId,
        });
        return;
      }
      versions.forEach((set, index) => options.push({
        key: `set-${set.id}`,
        label: this.optionLabel(settingId, set, versions.length > 1, index === 0),
        setId: set.id, settingId,
      }));
    });
    this.options = options;
  }

  private settingName(settingId: number): string {
    return this.lookup.settings.find((s) => s.id === settingId)?.settingName || `Setting ${settingId}`;
  }

  /** `At home — Submitted 04 Aug 2026 (v2, current)`. The version suffix appears only where there is more than one. */
  private optionLabel(settingId: number, set: RiskAssessment, versioned: boolean, current: boolean): string {
    const name = this.settingName(settingId);
    const suffix = versioned ? ` (v${set.versionNumber}, ${current ? 'current' : 'superseded'})` : '';
    if (set.submitted) {
      return `${name} — Submitted ${this.formatDate(set.submittedDate)}${suffix}`;
    }
    const mapped = this.mappedRisks(settingId).length;
    const answered = this.answeredInSet(set, settingId);
    return `${name} — Draft (${answered} of ${mapped} answered)${suffix}`;
  }

  /**
   * How many of the setting's mapped risks the dropdown should report as answered. Rows with no saved
   * answer render as Yes (§3 rule 3), so they count — otherwise the label would contradict the form.
   */
  private answeredInSet(set: RiskAssessment, settingId: number): number {
    const mapped = this.mappedRisks(settingId);
    return mapped.filter((definition) => {
      const answer = set.answers.find((a) => a.riskDefinitionId === definition.id);
      return answer ? answer.isCovered != null : true;
    }).length;
  }

  /**
   * The key of the assessment actually on screen. NOT the same as `selectedKey`: `[(ngModel)]`
   * writes `selectedKey` before `(ngModelChange)` fires, so comparing a new selection against
   * `selectedKey` would match every time and swallow the change.
   */
  private get renderedKey(): string | null {
    if (this.currentSetId != null) {
      return `set-${this.currentSetId}`;
    }
    return this.currentSettingId == null ? null : `new-${this.currentSettingId}`;
  }

  /** Selection change — confirm-discard first when the rendered draft has unsaved changes. */
  public onSelectionChange(newKey: string | null): void {
    if (newKey == null || newKey === this.renderedKey) {
      return;
    }
    if (this.currentSettingId != null && !this.readonly && this.isDirty()) {
      const previousKey = this.renderedKey;
      const confirmRef = this.dialog.open(DynamicConfirmModalComponent, {
        data: { confirmMessage: 'You have unsaved changes on this assessment. Discard them and switch?' },
        panelClass: 'my-confirm-dialog',
      });
      confirmRef.afterClosed().subscribe((confirm: boolean) => {
        if (confirm) {
          this.selectOption(newKey);
        } else {
          this.selectedKey = previousKey; // snap the dropdown back to what is on screen
        }
      });
      return;
    }
    this.selectOption(newKey);
  }

  /**
   * New — supersede the rendered (submitted) assessment with a fresh draft for the same setting. The
   * old assessment is untouched and stays in the dropdown.
   */
  public startNew(): void {
    const set = this.currentSet;
    if (this.saving || !this.canStartNew || !set || set.riskSettingId == null) {
      return;
    }
    const settingId = set.riskSettingId;
    const confirmRef = this.dialog.open(DynamicConfirmModalComponent, {
      data: {
        confirmMessage: `Create a new risk assessment for ${this.settingName(settingId)}? `
          + 'The existing assessment will be kept and stays available to view.',
      },
      panelClass: 'my-confirm-dialog',
    });
    confirmRef.afterClosed().subscribe((confirm: boolean) => {
      if (!confirm) {
        return;
      }
      this.saving = true;
      this.riskAssessmentService.createNew(this.data.placementId, settingId).subscribe({
        next: (newId) => {
          this.saving = false;
          this.refreshSets(newId);
        },
        error: (error) => {
          this.saving = false;
          this.errorBannerText = error.message;
          this.refreshSets(); // someone else may have created it — show server truth
        },
      });
    });
  }

  public setAnswer(row: RiskRow, covered: boolean): void {
    if (this.readonly) {
      return;
    }
    row.isCovered = covered;
  }

  public addCustomRow(): void {
    this.customRows.push({
      riskDefinitionId: null, riskDescription: '', controlMeasures: '',
      riskLevel: null, custom: true, isCovered: null, additionalMeasures: '', showMeasures: false,
    });
  }

  public removeCustomRow(index: number): void {
    this.customRows.splice(index, 1);
  }

  /** Draft save — no validation beyond the setting being chosen; partial answers are fine. */
  public save(): void {
    if (this.saving || this.readonly || this.isSuperseded || this.currentSettingId == null) {
      return;
    }
    this.saving = true;
    this.riskAssessmentService.save(this.buildPayload()).subscribe({
      next: () => {
        this.saving = false;
        this.lastSavedAt = new Date();
        this.errorBannerText = '';
        this.refreshSets();
      },
      error: (error) => {
        this.saving = false;
        this.errorBannerText = error.message;
        if ((error.message || '').toLowerCase().includes('locked')) {
          this.refreshSets(); // lost the race — someone submitted this set; re-render view-only
        }
      },
    });
  }

  /**
   * Submit — gated only by the declaration (§3 rule 9); every other rule is the server's. A validation
   * 400 keeps the user's rows and shows the reasons + outlines; a locked 400 (lost race) refreshes into
   * view-only. The declaration flag is sent as the educator actually left it — never hardcoded true.
   */
  public submit(): void {
    if (this.saving || this.readonly || this.isSuperseded || this.currentSettingId == null
      || !this.declarationAccepted) {
      return;
    }
    this.saving = true;
    this.showOutlines = false;
    this.riskAssessmentService.submit({ ...this.buildPayload(), declarationAccepted: this.declarationAccepted }).subscribe({
      next: () => {
        this.saving = false;
        this.errorBannerText = '';
        this.refreshSets();
      },
      error: (error) => {
        this.saving = false;
        this.errorBannerText = error.message;
        this.showOutlines = true;
        if ((error.message || '').toLowerCase().includes('locked')) {
          this.refreshSets();
        }
      },
    });
  }

  /** Unlock — offered on the submitted CURRENT set; non-consultants get the server's 403. */
  public unlock(): void {
    const set = this.currentSet;
    if (!set || !set.submitted || this.isSuperseded) {
      return;
    }
    this.riskAssessmentService.unlock(set.id).subscribe({
      next: () => this.refreshSets(),
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  /**
   * Downloads the assessment on screen as a PDF, as it was last saved (unsaved edits are not in it). Offered for an
   * assessment that exists, while riskassessment.pdf is on.
   */
  public downloadPdf(): void {
    const set = this.currentSet;
    if (!this.pdfEnabled || !set || this.downloadingPdf) {
      return;
    }
    this.downloadingPdf = true;
    this.riskAssessmentService.downloadPdf(set.id).subscribe({
      next: (blob: Blob) => {
        this.downloadingPdf = false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `risk-assessment-${this.data.placementId}-v${set.versionNumber || 1}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.downloadingPdf = false;
        this.errorModalHelper.show(error.message, error);
      },
    });
  }

  /** Row outline — mirrors the server's submit rules for display only; never blocks Submit. */
  public rowInvalid(row: RiskRow): boolean {
    if (!this.showOutlines || this.readonly) {
      return false;
    }
    if (row.custom) {
      if (!this.customRowHasContent(row)) {
        return false; // empty custom rows are excluded from the payload — nothing to flag
      }
      if (!row.riskDescription?.trim()) {
        return true;
      }
    }
    if (row.isCovered === null) {
      return true;
    }
    return row.isCovered === false && !row.additionalMeasures?.trim();
  }

  /**
   * Close — confirm-discard when the current draft has unsaved changes. Reached by the Close button and
   * by Escape, so it guards against a second confirm while one is already open (Escape is easy to mash).
   */
  public close(): void {
    if (this.confirmingClose) {
      return;
    }
    if (this.isDirty()) {
      this.confirmingClose = true;
      const confirmRef = this.dialog.open(DynamicConfirmModalComponent, {
        data: { confirmMessage: 'You have unsaved changes. Discard them and close?' },
        panelClass: 'my-confirm-dialog',
      });
      confirmRef.afterClosed().subscribe((confirm: boolean) => {
        this.confirmingClose = false;
        if (confirm) {
          this.dialogRef.close(this.sets);
        }
      });
      return;
    }
    this.dialogRef.close(this.sets);
  }

  /**
   * On-open preselect rule (spec §5): the most recently updated draft, otherwise the most recently
   * submitted CURRENT assessment, otherwise none (picker only). A superseded version is never
   * preselected — it is historical evidence, not somewhere to work.
   */
  private initialKey(sets: RiskAssessment[]): string | null {
    const candidates = sets.filter((s) => s.riskSettingId != null);
    const drafts = candidates.filter((s) => !s.submitted);
    if (drafts.length > 0) {
      return `set-${this.newest(drafts, (s) => s.updatedDate || s.createdDate).id}`;
    }
    const currents = candidates.filter((s) => this.versionsOf(s.riskSettingId).length > 0
      && this.versionsOf(s.riskSettingId)[0].id === s.id);
    if (currents.length > 0) {
      return `set-${this.newest(currents, (s) => s.submittedDate).id}`;
    }
    return null;
  }

  private newest(sets: RiskAssessment[], dateOf: (s: RiskAssessment) => string | undefined): RiskAssessment {
    return sets.reduce((best, s) => (this.timeOf(dateOf(s)) > this.timeOf(dateOf(best)) ? s : best));
  }

  private timeOf(value: string | undefined): number {
    return value ? new Date(value).getTime() : 0;
  }

  private selectOption(key: string): void {
    const option = this.options.find((o) => o.key === key);
    if (!option) {
      return;
    }
    this.selectedKey = key;
    this.currentSetId = option.setId;
    this.currentSettingId = option.settingId;
    this.errorBannerText = '';
    this.showOutlines = false;
    this.declarationAccepted = false; // never carried across assessments, and never pre-ticked
    this.buildRows();
    this.savedSnapshot = this.snapshot();
  }

  /**
   * Editable drafts render from the LOOKUP (active mapped risks) merged with saved answers;
   * submitted sets render from their own ANSWERS (enriched historical text) — never from lookup.
   */
  private buildRows(): void {
    const set = this.currentSet;
    if (set && set.submitted) {
      this.standardRows = set.answers
        .filter((a) => a.riskDefinitionId != null)
        .map((a) => ({
          riskDefinitionId: a.riskDefinitionId ?? null, riskDescription: a.riskDescription || '',
          controlMeasures: a.controlMeasures || '', riskLevel: a.riskLevel || null,
          custom: false, isCovered: a.isCovered ?? null,
          additionalMeasures: a.additionalMeasures || '', showMeasures: false,
        }));
      this.customRows = set.answers
        .filter((a) => a.riskDefinitionId == null)
        .map((a) => this.customRowFromAnswer(a));
      return;
    }

    const saved = new Map<number, RiskAssessmentAnswer>();
    (set?.answers || [])
      .filter((a) => a.riskDefinitionId != null)
      .forEach((a) => saved.set(a.riskDefinitionId as number, a));

    this.standardRows = this.mappedRisks(this.currentSettingId).map((definition) => {
      const answer = saved.get(definition.id);
      return {
        riskDefinitionId: definition.id, riskDescription: definition.riskDescription,
        controlMeasures: definition.controlMeasures, riskLevel: definition.riskLevel || null,
        custom: false,
        // No saved answer ⇒ Yes (2026-08-04): a new assessment opens fully answered and the educator
        // changes only the rows that need No. A saved answer always wins, so a saved No is never lost.
        isCovered: answer ? answer.isCovered ?? null : true,
        additionalMeasures: answer?.additionalMeasures || '', showMeasures: false,
      };
    });
    this.customRows = (set?.answers || [])
      .filter((a) => a.riskDefinitionId == null)
      .map((a) => this.customRowFromAnswer(a));
  }

  private customRowFromAnswer(answer: RiskAssessmentAnswer): RiskRow {
    return {
      riskDefinitionId: null, riskDescription: answer.customRiskDescription || '',
      controlMeasures: answer.customControlMeasures || '', riskLevel: null,
      custom: true, isCovered: answer.isCovered ?? null,
      additionalMeasures: answer.additionalMeasures || '', showMeasures: false,
    };
  }

  private mappedRisks(settingId: number | null): RiskDefinition[] {
    if (settingId == null) {
      return [];
    }
    return this.lookup.risks.filter((r) => r.isActive && r.settingIds.includes(settingId));
  }

  /** The wholesale save/submit payload — identity fields only; the server owns tenant + actor. */
  private buildPayload(): RiskAssessment {
    return {
      id: this.currentSetId || 0,
      placementId: this.data.placementId,
      riskSettingId: this.currentSettingId,
      submitted: false,
      answers: [
        ...this.standardRows.map((r) => ({
          riskDefinitionId: r.riskDefinitionId,
          isCovered: r.isCovered,
          additionalMeasures: r.additionalMeasures || null,
        })),
        ...this.customRows
          .filter((r) => this.customRowHasContent(r))
          .map((r) => ({
            customRiskDescription: r.riskDescription || null,
            customControlMeasures: r.controlMeasures || null,
            isCovered: r.isCovered,
            additionalMeasures: r.additionalMeasures || null,
          })),
      ],
    } as RiskAssessment;
  }

  private customRowHasContent(row: RiskRow): boolean {
    return !!(row.riskDescription?.trim() || row.controlMeasures?.trim()
      || row.isCovered !== null || row.additionalMeasures?.trim());
  }

  private isDirty(): boolean {
    return this.currentSettingId != null && !this.readonly && this.snapshot() !== this.savedSnapshot;
  }

  private snapshot(): string {
    return JSON.stringify(this.buildPayload());
  }

  /** Re-pull the placement's sets and re-render. Pass a set id to select it (e.g. a new version). */
  private refreshSets(selectSetId?: number): void {
    this.riskAssessmentService.getByPlacement(this.data.placementId).subscribe({
      next: (sets) => {
        this.sets = sets;
        this.buildOptions();
        if (selectSetId != null) {
          this.selectOption(`set-${selectSetId}`);
          return;
        }
        // A first save turns a `new-{settingId}` selection into a real assessment — adopt its id so the
        // dropdown key and the payload stop disagreeing with the server.
        if (this.currentSetId == null && this.currentSettingId != null) {
          const created = this.versionsOf(this.currentSettingId)[0];
          if (created) {
            this.selectOption(`set-${created.id}`);
            return;
          }
        }
        if (this.currentSettingId != null) {
          this.buildRows();
          this.savedSnapshot = this.snapshot();
        }
      },
      error: (error) => this.errorModalHelper.show(error.message, error),
    });
  }

  private formatDate(value: string | undefined): string {
    if (!value) {
      return '';
    }
    return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
}
